import { ButtonLink } from '@/features/shared/elements/Link/Buttons'
import { spacing } from '@/features/shared/theme/tokens'
import { screenSizes } from '@shipt/design-system-themes'
import { Headline, Body } from '@shipt/design-system-typography'
import styled from 'styled-components'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { type StorySubsectionEmbedded } from '@/features/cms/components/CMS/types'
import { getBoxShadow } from '@/features/shared/styles/effects'
import { theme } from '@/features/shared/constants/shiptTheme'

export const MultiStoryCards = ({
  stories,
  hasCTA,
  isCarousel,
}: {
  stories: StorySubsectionEmbedded[]
  hasCTA: boolean
  isCarousel: boolean
}) =>
  stories.map((story) => {
    const { heading, align, image_type, image, subheading, cta_link } = story.data
    const isAvatar = image_type === 'avatar'
    const isStart = align === 'start'
    return (
      <Story
        key={heading}
        $align={align}
        data-has-cta={hasCTA}
        $imageType={image_type}
        data-is-carousel={isCarousel}
      >
        <StoryImage
          alt={heading}
          src={image.src}
          width={isAvatar ? 64 : 343}
          height={isAvatar ? 64 : 219}
          style={{
            objectFit: 'cover',
            ...(isAvatar && {
              width: '64px',
              height: '64px',
            }),
          }}
          data-image-type={image_type}
        />
        <StoryContent>
          <div>
            <StoryHeading as="h3" align={isStart ? 'left' : 'center'}>
              {heading}
            </StoryHeading>
            {!!subheading && (
              <StorySubheading align={isStart ? 'left' : 'center'}>{subheading}</StorySubheading>
            )}
          </div>
          {!!cta_link && (
            <StoryLink
              href={cta_link.url}
              variant="secondary"
              size="md"
              opensInNewTab={cta_link.open_in_new_tab}
            >
              {cta_link.text}
            </StoryLink>
          )}
        </StoryContent>
      </Story>
    )
  })

const StoryImage = styled(Image)<{ 'data-image-type': 'avatar' | 'image' }>`
  width: 100%;
  border-radius: 0.5rem;

  &[data-image-type='avatar'] {
    border-radius: 100%;
    border: 1px solid ${theme.color.border.base.neutral.soft};
  }
`

const StoryContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: inherit;
  padding: 0;
`

const Story = styled.div<{
  $align: 'start' | 'center'
  'data-has-cta': boolean
  $imageType: 'avatar' | 'image'
  'data-is-carousel'?: boolean
}>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $align }) => $align};
  width: 100%;
  max-width: 21.4375rem;
  padding: ${spacing(0, 'lg')};

  &[data-is-carousel='true'] {
    width: 17.5rem;
    margin: 1rem 0 1.5rem;
  }

  &[data-has-cta='true'] {
    background-color: #ffffff;
    ${getBoxShadow('lift', 'light', false)};
    border-radius: 0.5rem;
    padding: ${({ $imageType }) => ($imageType === 'image' ? '0' : '1.5rem')};

    ${StoryImage} {
      border-radius: ${({ $imageType }) => ($imageType === 'image' ? '0.5rem 0.5rem 0 0' : '100%')};
    }
    ${StoryContent} {
      padding: ${({ $imageType }) => ($imageType === 'avatar' ? '0' : '0 1.5rem 1.5rem')};
      justify-content: space-between;
      flex-grow: 1;
    }
  }
`

const StoryHeading = styled(Headline)`
  font-size: 1.125rem;
  margin-top: ${spacing('xl')};

  @supports (text-wrap: pretty) {
    text-wrap: pretty;
  }

  @supports not (text-wrap: pretty) {
    white-space: normal;
  }
`

const StorySubheading = styled(Body)`
  margin: ${spacing('lg', 0, 'xxl')};
  font-size: 1rem;

  @media ${screenSizes.tablet} {
    margin-bottom: ${spacing(0)};
  }

  @media ${screenSizes.smDesktop} {
    margin: ${spacing('lg', 0, 0)};
  }
`

const StoryLink = styled(ButtonLink)`
  @media ${screenSizes.tablet} {
    margin-top: ${spacing('lg')};
  }

  @media ${screenSizes.max_mobile} {
    width: 100%;
    max-width: 100%;
  }
`
